import { useEffect, useRef, useState } from "react";
import { api, downscaleImage, errMsg, M_PRIORITY, M_TIPO } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ImagePlus, Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  tipo: "reclamacao", assunto: "", descricao: "", area_id: "", local_livre: "",
  prioridade: "media", attachments: [], is_anonimo: false,
  eql_id: null, eql_label: "", morador_id: null,
  solicitante_nome: "", solicitante_email: "", solicitante_telefone: "",
};

const moradorPhone = (m) => [m.ddi_celular, m.celular].filter(Boolean).join(" ") || m.telefone || "";

export default function ManifestationFormDialog({ open, onOpenChange, editing, areas, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [eqlOpen, setEqlOpen] = useState(false);
  const [moradores, setMoradores] = useState(null);
  const [diag, setDiag] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMoradores(null);
    if (editing) {
      setForm({
        tipo: editing.tipo, assunto: editing.assunto, descricao: editing.descricao,
        area_id: editing.area_id || "", local_livre: editing.local_livre || "",
        prioridade: editing.prioridade, attachments: editing.attachments || [],
        is_anonimo: !!editing.is_anonimo,
        eql_id: editing.eql_id, eql_label: editing.eql_label || "", morador_id: editing.morador_id,
        solicitante_nome: editing.solicitante_nome || "",
        solicitante_email: editing.solicitante_email || "",
        solicitante_telefone: editing.solicitante_telefone || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, editing]);

  const selectEql = async (eql) => {
    const label = eql.descricao || eql.numero || eql.id;
    setForm((f) => ({ ...f, eql_id: eql.id, eql_label: label, morador_id: null }));
    setEqlOpen(false);
    setMoradores(null);
    const eqlParam = eql.descricao || eql.numero || eql.id;
    try {
      const { data } = await api.get("/tenant/brave/moradores", { params: { eql: eqlParam } });
      const lista = data.moradores || [];
      setMoradores(lista);
      if (!lista.length) {
        setDiag({
          titulo: "Nenhum morador retornado para a EQL",
          texto: JSON.stringify({ endpoint: data.debug?.endpoint, params: data.debug?.params, total: data.debug?.total, count: data.debug?.count }, null, 2),
        });
      }
    } catch (err) {
      setMoradores([]);
      setDiag({
        titulo: "Falha ao consultar moradores na API Brave Access",
        texto: errMsg(err),
      });
    }
  };

  const selectMorador = (id) => {
    const m = (moradores || []).find((x) => x.id === id);
    if (!m) return;
    setForm((f) => ({
      ...f, morador_id: m.id,
      solicitante_nome: m.nome || f.solicitante_nome,
      solicitante_email: m.email || f.solicitante_email,
      solicitante_telefone: moradorPhone(m) || f.solicitante_telefone,
    }));
  };

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files.slice(0, 10 - form.attachments.length)) {
        const optimized = f.type.startsWith("image/") ? await downscaleImage(f) : f;
        const fd = new FormData();
        fd.append("file", optimized);
        const { data } = await api.post("/upload", fd);
        setForm((prev) => ({ ...prev, attachments: [...prev.attachments, data.storage_path] }));
      }
      toast.success("Anexo(s) enviado(s)");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const norm = (v) => (v === undefined || v === null ? "" : v);
        const diff = {};
        for (const key of Object.keys(form)) {
          if (key === "attachments") {
            if (JSON.stringify(form.attachments) !== JSON.stringify(editing.attachments || [])) diff.attachments = form.attachments;
          } else if (typeof form[key] === "boolean") {
            if (form[key] !== !!editing[key]) diff[key] = form[key];
          } else if (norm(form[key]) !== norm(editing[key])) {
            diff[key] = form[key];
          }
        }
        if (!Object.keys(diff).length) {
          toast.info("Nenhuma alteração para salvar");
          onOpenChange(false);
          return;
        }
        await api.patch(`/tenant/manifestacoes/${editing.id}`, diff);
        toast.success("Registro atualizado");
      } else {
        await api.post("/tenant/manifestacoes", form);
        toast.success("Pedido/Ocorrência registrado");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[90vh] overflow-y-auto" data-testid="manif-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{editing ? "Editar Pedido/Ocorrência" : "Novo Pedido/Ocorrência"}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Centralize solicitações, reclamações, sugestões e elogios com registro auditado.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="manif-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Tipo *</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="manif-tipo-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {Object.entries(M_TIPO).map(([k, label]) => (
                      <SelectItem key={k} data-testid={`manif-tipo-${k}`} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Prioridade</Label>
                <Select value={form.prioridade} onValueChange={(v) => setForm({ ...form, prioridade: v })}>
                  <SelectTrigger data-testid="manif-prioridade-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    {Object.entries(M_PRIORITY).map(([k, label]) => (
                      <SelectItem key={k} data-testid={`manif-prioridade-${k}`} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Assunto *</Label>
              <Input data-testid="manif-assunto-input" required value={form.assunto} maxLength={150}
                onChange={(e) => setForm({ ...form, assunto: e.target.value })}
                placeholder='Ex.: "Barulho excessivo após 22h"'
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Descrição detalhada *</Label>
              <Textarea data-testid="manif-descricao-input" required value={form.descricao} rows={4} maxLength={2000}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Descreva o ocorrido com detalhes…"
                className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Local da ocorrência (Áreas e Locais)</Label>
                <Select value={form.area_id || "none"} onValueChange={(v) => setForm({ ...form, area_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="manif-area-select" className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md">
                    <SelectValue placeholder="Selecione…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <SelectItem data-testid="manif-area-none" value="none">Não informado</SelectItem>
                    {areas.map((a) => (
                      <SelectItem key={a.id} data-testid={`manif-area-${a.id}`} value={a.id}>{a.code} — {a.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Complemento do local</Label>
                <Input data-testid="manif-local-input" value={form.local_livre} maxLength={200}
                  onChange={(e) => setForm({ ...form, local_livre: e.target.value })}
                  placeholder='Ex.: "Bloco B, 3º andar"'
                  className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
              </div>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="manif-solicitante-section">
              <p className="text-xs font-medium text-zinc-400">Solicitante</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">EQL (unidade)</Label>
                  <button type="button" data-testid="manif-eql-button" onClick={() => setEqlOpen(true)}
                    className="mt-1.5 w-full h-9 px-3 text-sm text-left bg-zinc-900 border border-zinc-800 rounded-md hover:border-indigo-500/60 transition-colors duration-200">
                    <span className={form.eql_label ? "text-zinc-100" : "text-zinc-500"}>
                      {form.eql_label || "Selecionar EQL…"}
                    </span>
                  </button>
                </div>
                {moradores && moradores.length > 0 && (
                  <div>
                    <Label className="text-xs text-zinc-400">Morador (base Brave Access)</Label>
                    <Select value={form.morador_id || ""} onValueChange={selectMorador}>
                      <SelectTrigger data-testid="manif-morador-select" className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md">
                        <SelectValue placeholder="Selecione o morador…" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                        {moradores.map((m) => (
                          <SelectItem key={m.id} data-testid={`manif-morador-${m.id}`} value={m.id}>{m.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-zinc-400">Nome *</Label>
                  <Input data-testid="manif-nome-input" required value={form.solicitante_nome} maxLength={120}
                    onChange={(e) => setForm({ ...form, solicitante_nome: e.target.value })}
                    className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">E-mail</Label>
                  <Input data-testid="manif-email-input" type="email" value={form.solicitante_email} maxLength={200}
                    onChange={(e) => setForm({ ...form, solicitante_email: e.target.value })}
                    className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Telefone</Label>
                  <Input data-testid="manif-telefone-input" value={form.solicitante_telefone} maxLength={40}
                    onChange={(e) => setForm({ ...form, solicitante_telefone: e.target.value })}
                    className="mt-1.5 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-md focus:border-indigo-500" />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer" data-testid="manif-anonimo-label">
                <Checkbox data-testid="manif-anonimo-checkbox" checked={form.is_anonimo}
                  onCheckedChange={(v) => setForm({ ...form, is_anonimo: !!v })} />
                Registrar como anônimo (oculta o nome na listagem)
              </label>
            </div>

            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-3" data-testid="manif-anexos-section">
              <p className="text-xs font-medium text-zinc-400">Anexos (fotos, vídeos ou documentos — até 10)</p>
              {form.attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {form.attachments.map((p, i) => (
                    <span key={p} data-testid={`manif-anexo-${i}`}
                      className="inline-flex items-center gap-1 text-xs bg-zinc-800 text-zinc-300 rounded-full px-2 py-1">
                      <Paperclip className="h-3 w-3" /> Anexo {i + 1}
                      <button type="button" data-testid={`manif-anexo-remove-${i}`} title="Remover anexo"
                        onClick={() => setForm((f) => ({ ...f, attachments: f.attachments.filter((x) => x !== p) }))}
                        className="hover:text-rose-400 transition-colors duration-150">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input ref={fileRef} type="file" multiple accept="image/*,video/*,application/pdf" className="hidden"
                data-testid="manif-file-input" onChange={pickFiles} />
              <Button type="button" data-testid="manif-anexar-button" variant="outline" disabled={uploading || form.attachments.length >= 10}
                onClick={() => fileRef.current?.click()}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ImagePlus className="h-4 w-4 mr-1.5" />}
                Anexar arquivos
              </Button>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" data-testid="manif-cancel-button" variant="ghost" onClick={() => onOpenChange(false)}
                className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
                Cancelar
              </Button>
              <Button data-testid="manif-save-button" type="submit" disabled={saving || uploading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salvar alterações" : "Registrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={eqlOpen} onOpenChange={setEqlOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="manif-eql-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Selecionar EQL</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Busque a unidade do solicitante na base Brave Access.
            </DialogDescription>
          </DialogHeader>
          <SearchableSelect onSelect={selectEql} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!diag} onOpenChange={(v) => !v && setDiag(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="manif-moradores-diag-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">{diag?.titulo}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Detalhes técnicos da consulta — copie e envie à equipe Brave Access para revisão da API. Você também pode preencher os dados do solicitante manualmente.
            </DialogDescription>
          </DialogHeader>
          <pre data-testid="manif-moradores-diag-text"
            className="text-xs bg-zinc-950 border border-zinc-800 rounded-md p-3 overflow-auto max-h-64 whitespace-pre-wrap text-zinc-300">{diag?.texto}</pre>
          <div className="flex justify-end gap-2">
            <Button type="button" data-testid="manif-moradores-diag-close" variant="ghost" onClick={() => setDiag(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Fechar
            </Button>
            <Button type="button" data-testid="manif-moradores-diag-copy"
              onClick={() => { navigator.clipboard?.writeText(diag?.texto || ""); toast.success("Detalhes copiados"); }}
              className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
              Copiar detalhes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
